import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAxiosInstance } from "../../apis/index";

// The banner lives on its own endpoint so campaigns can be managed independently of
// basic-settings. Contract: docs/promo-banner-spec.md
export const BANNER_ENDPOINT = "banners";

const initialState = {
  status: "idle",
  error: null,
  message: null,
  bannerData: null,
};

// The API returns a flat list of rows using its own field names. Everything below the
// slice - the hook, the carousel, the slide - works on one stable shape, so the mapping
// happens once, here. Only this function changes if the backend renames a field.
const normalizeSlide = (item, index) => ({
  id: item.id ?? index,
  type: String(item.media_type || "image").toLowerCase(),
  media_desktop_url: item.desktop_media_url || null,
  media_mobile_url: item.mobile_media_url || null,
  // Not sent today; video slides fall back to their own first frame without them.
  poster_desktop_url: item.desktop_poster_url || null,
  poster_mobile_url: item.mobile_poster_url || null,
  alt_text: item.content || "",
  link_url: item.link || null,
  link_target: item.link_target === "_blank" ? "_blank" : "_self",
});

export const normalizeBanner = (payload) => {
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const slides = rows
    .filter((item) => Number(item.is_active) === 1)
    .map(normalizeSlide);

  // No live rows is the same thing as no banner - the carousel renders nothing.
  if (!slides.length) return null;

  return {
    status: 1,
    // Optional; the carousel falls back to its own default when absent.
    autoplay_delay: payload?.autoplay_delay,
    slides,
  };
};

export const getBanner = createAsyncThunk(
  "banner/getBanner",
  async (lang, { rejectWithValue }) => {
    const api = await getAxiosInstance();
    try {
      const response = await api.get(`${BANNER_ENDPOINT}?language=${lang}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const bannerSlice = createSlice({
  name: "banner",
  initialState,
  reducers: {},
  extraReducers: {
    [getBanner.pending]: (state) => {
      state.status = "loading";
      state.message = null;
    },
    [getBanner.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.message = action.payload?.message ?? null;
      state.bannerData = normalizeBanner(action.payload);
    },
    [getBanner.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload ?? null;
      state.bannerData = null;
    },
  },
});

export default bannerSlice.reducer;
